import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

// Assets


// Utils
import { UserAuth } from "../../../utils/AuthContext";
import useRequest from "../../../utils/useRequest";
import usePrint from "../../../utils/hooks/usePrint";
import { DataContext } from "../../../utils/DataContext";

// Components
import PageWrapper from "../../../components/PageWrapper";
import ItemInvoiceFooter from "./ItemInvoiceFooter";
import BalanceBoxes from "../../../components/BalanceBoxes";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../../components/table-component/RoundedTable";
import TextDiv from "../../../components/TextDiv";
import Image from "../../../components/Image";
import Loader from "../../../components/Loader";
import InformationBox from "../../../components/InformationBox";
import CustomButton from "../../../components/CustomButton";
import ConfirmationBox from "../../../components/ConfirmationBox";

export default function PreviewItemInvoiceTemplate() {
  const { t } = useTranslation("general");
  const location = useLocation();
  const dataFromNavigation = location.state?.data;
  const { userData } = useContext(UserAuth);
  const { selectedSubCompany } = useContext(DataContext);
  const navigate = useNavigate();

  const printRef = useRef(null);

  const { invoiceHeaderImage, invoiceFooterImage } = dataFromNavigation || {};

  const [loadingAcceptFile, setLoadingAcceptFile] = useState(false);
  const [acceptedFiles, setAcceptedFiles] = useState({});
  const [countdown, setCountdown] = useState(null);

  const redirectDuration = 3000;

  // Stored in DB
  const [storedInvoiceHeader, setStoredInvoiceHeader] = useState(null);
  const [storedInvoiceFooter, setStoredInvoiceFooter] = useState(null);

  const {
    loading,
    error,
    data,
    refetch: getItemInvoiceTemplate,
  } = useRequest(
    `/getItemInvoiceTemplate`,
    "GET",
    null,
    {
      getInvoiceHeader: !invoiceHeaderImage,
      getInvoiceFooter: !invoiceFooterImage,
      subCompanyId: selectedSubCompany?.id,
      organizationId: userData?.organizationId,
    },
    false
  );

  useEffect(() => {
    if (invoiceHeaderImage) {
      setAcceptedFiles({
        invoiceHeader: "exist",
      });
    }
    if (invoiceFooterImage) {
      setAcceptedFiles((prev) => ({
        ...prev,
        invoiceFooter: "exist",
      }));
    }

    if (invoiceHeaderImage && invoiceFooterImage) return;
    getItemInvoiceTemplate();
  }, []);

  useEffect(() => {
    data?.documents?.map((document) => {
      if (document?.image_type === "invoiceHeader") {
        setStoredInvoiceHeader(document);
      } else if (document?.image_type === "invoiceFooter") {
        setStoredInvoiceFooter(document);
      }
    });

    if (!data?.documents?.length) {
      setStoredInvoiceHeader(null);
      setStoredInvoiceFooter(null);
    }
  }, [data]);

  useEffect(() => {
    const allUploaded = Object.values(acceptedFiles).every(
      (status) => status === "uploaded"
    );

    if (Object.keys(acceptedFiles).length > 0 && allUploaded) {
      setCountdown(redirectDuration / 1000); // Start countdown in seconds

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            clearInterval(countdownInterval);
            navigate("/");
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [acceptedFiles]);

  const handleFileUpload = async (file, documentType) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("images", file);
    formData.append(
      "invoiceId",
      `${userData?.organizationId}---${selectedSubCompany?.id}`
    );
    formData.append("invoiceType", documentType);

    if (documentType === "invoiceHeader") {
      setAcceptedFiles((prev) => ({ ...prev, invoiceHeader: "uploaded" }));
    } else if (documentType === "invoiceFooter") {
      setAcceptedFiles((prev) => ({ ...prev, invoiceFooter: "uploaded" }));
    }

    try {
      setLoadingAcceptFile(true);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/uploadInvoiceImages`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setLoadingAcceptFile(false);
    }
  };

  const tableTitles = [
    { value: "sequence" },
    { value: "productName" },
    { value: "numberOfBoxes" },
    { value: "price" },
    { value: "length" },
    { value: "width" },
    { value: "totalPrice" },
  ];

  const tableRows = [
    [
      { value: 1, type: TABLE_CELL_TYPES.TEXT },
      { value: "-", type: TABLE_CELL_TYPES.TEXT },
      { value: "-", type: TABLE_CELL_TYPES.TEXT },
      { value: "-", type: TABLE_CELL_TYPES.MONEY },
      { value: "-", type: TABLE_CELL_TYPES.TEXT },
      { value: "-", type: TABLE_CELL_TYPES.TEXT },
      { value: "-", type: TABLE_CELL_TYPES.TEXT },
    ],
  ];

  const { handlePrint } = usePrint();

  const print = () => handlePrint(printRef.current.innerHTML);

  return (
    <PageWrapper withBackButton ref={printRef}>
      <div className="u-padding--bottom_500">
        {invoiceHeaderImage?.preview?.url ? (
          <div className="o-row u-margin--top_300 u-margin--bottom_300">
            <img
              src={invoiceHeaderImage.preview.url}
              alt="Invoice Header Preview"
              className="u-width_full-width u-height-specific_300 object-fit_cover"
            />
          </div>
        ) : storedInvoiceHeader ? (
          <Image
            className="u-width_full-width u-margin--top_300 u-margin--bottom_300"
            imgClasses="u-width_full-width u-height-specific_300 object-fit_cover"
            imagePath={storedInvoiceHeader?.path}
            mode="show"
          />
        ) : loading ? (
          <Loader />
        ) : (
          <InformationBox>{t("noInvoiceHeaderUploaded")}</InformationBox>
        )}
        <div className="o-column u-padding--bottom_500">
          {/* Body section 1 */}
          <section className="item-invoice">
            {/* Column 1 */}
            <div>
              <TextDiv
                title={"customerName"}
                value={"-"?.name}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2"
              />

              <TextDiv
                title={"phoneNumber"}
                value={"-"?.phoneNumber}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2"
              />

              <TextDiv
                title={"address"}
                value={"-"?.address}
                divClasses="item-invoice-div"
                spanClasses="u-flex_flex-2"
              />
            </div>

            {/* Column 2 */}
            <div>
              <TextDiv
                title={"invoiceNumber"}
                value={"-"}
                divClasses="item-invoice-div"
              />
              <TextDiv
                title={"invoiceType"}
                value={t("-")}
                divClasses="item-invoice-div"
              />
              <TextDiv title="Date" value={"-"} divClasses="item-invoice-div" />
            </div>
          </section>

          {/* Body section 2 */}
          <section className="u-margin--top_500 u-margin--bottom_500">
            <RoundedTable titles={tableTitles} rows={tableRows} />
          </section>

          {/* Body section 3 */}
          <section className="u-margin--top_500 u-margin--bottom_500">
            <TextDiv
              title={t("notes")}
              value={"-"}
              divClasses="item-invoice-div o-row--vertical-align_center"
            />
          </section>

          {/* Body section 4 */}
          <section className="o-row o-row--horizontal-align_space-between u-width_full-width u-margin--top_500 u-margin--bottom_500">
            <BalanceBoxes
              notAlignedEnd={true}
              boxes={[
                { title: "discount", value: `- %` },
                { title: "received", value: "-" },
                { title: "left", value: "-" },
              ]}
            />
            <BalanceBoxes
              boxes={[
                { title: "totalAmount", value: "-" },
                { title: "netAmount", value: "-" },
                { title: "netAmount", value: "-" },
              ]}
            />
          </section>
        </div>

        {/* Footer Image Preview */}
        {invoiceFooterImage?.preview?.url ? (
          <div className="o-row u-margin--top_300 u-margin--bottom_300">
            <img
              src={invoiceFooterImage.preview.url}
              alt="Invoice Footer Preview"
              className="u-width_full-width u-height-specific_300 object-fit_cover"
            />
          </div>
        ) : storedInvoiceFooter ? (
          <Image
            className="u-width_full-width u-margin--top_300 u-margin--bottom_300"
            imgClasses="u-width_full-width u-height-specific_300 object-fit_cover"
            imagePath={storedInvoiceFooter?.path}
            mode="show"
          />
        ) : loading ? (
          <Loader />
        ) : (
          <InformationBox>{t("noInvoiceFooterUploaded")}</InformationBox>
        )}

        {loadingAcceptFile && <Loader />}
        {
          <>
            {acceptedFiles?.invoiceHeader === "uploaded" &&
            acceptedFiles?.invoiceFooter === "uploaded" ? (
              <ConfirmationBox>
                {t("bothDesignsAccepted") + ". "}
              </ConfirmationBox>
            ) : acceptedFiles?.invoiceHeader === "uploaded" &&
              invoiceHeaderImage?.preview?.url ? (
              <ConfirmationBox>{t("invoiceHeaderAccepted")}</ConfirmationBox>
            ) : acceptedFiles?.invoiceFooter === "uploaded" &&
              invoiceFooterImage?.preview?.url ? (
              <ConfirmationBox>{t("invoiceFooterAccepted")}</ConfirmationBox>
            ) : null}

            {countdown && (
              <InformationBox>
                {t("redirectWithin") + " "}
                {countdown} {t("seconds")}
              </InformationBox>
            )}
          </>
        }

        <div className="o-row o-row--horizontal-align_end o-row--gap-size_500">
          <CustomButton
            title={t("print")}
            onClick={print}
            variant="gray"
            icon="print"
            iconSize={18}
            disabled={false}
          />

          {dataFromNavigation?.headerFile && (
            <CustomButton
              title={t("acceptInvoiceHeader")}
              onClick={() =>
                handleFileUpload(
                  dataFromNavigation?.headerFile,
                  "invoiceHeader"
                )
              }
              icon="save"
              iconSize={16}
              disabled={acceptedFiles.invoiceHeader === "uploaded"}
            />
          )}

          {dataFromNavigation?.footerFile && (
            <CustomButton
              title={t("acceptInvoiceFooter")}
              onClick={() =>
                handleFileUpload(
                  dataFromNavigation?.footerFile,
                  "invoiceFooter"
                )
              }
              icon="save"
              iconSize={16}
              disabled={acceptedFiles.invoiceFooter === "uploaded"}
            />
          )}
        </div>

        <ItemInvoiceFooter />
      </div>
    </PageWrapper>
  );
}
